import { AxiosRequestConfig } from 'axios';

import { Ax } from '@/utils';
import store from '@/services/store';
import I18nTemplate from '@/services/i18n/template';

/**
 * Сохранение товара, выбранного в модалке ОКТРУ (ModalPlanningNew.vue), в нашу БД через sit-py.
 *
 * Данные приходят с внешних госсервисов (e-catalog.gov.kz), поэтому наши эндпоинты
 * рассчитаны на повторную отправку одних и тех же данных:
 *  - категория сохраняется через UPSERT по source_id (POST .../oktru/categories);
 *  - характеристика и её значение, а также связь с ЕНСТРУ - обычные INSERT,
 *    при повторной отправке сервер вернёт 409 (Conflict), что для нашего сценария не ошибка,
 *    а сигнал "уже сохранено ранее".
 */

const API_PY_BASE_URL = '/api-py/oktru';

// ---------------------------------------------------------------------------
// Тело запроса/ответа sit-py (поля - snake_case, как в контракте бэкенда)
// ---------------------------------------------------------------------------

export interface OktruCategoryPayload {
    source_id: number;
    parent_source_id?: number | null;
    name_ru: string;
    name_kk?: string | null;
    level?: number | null;
    full_code: string;
    status?: number | null;
    is_leaf?: boolean | null;
}

export interface OktruCategoryResponse extends OktruCategoryPayload {
    id: number;
}

export interface OktruAttributePayload {
    oktru_category_source_id: number;
    code: string;
    name_ru: string;
    name_kk?: string | null;
    data_type?: string | null;
    is_main?: boolean | null;
    is_new?: boolean | null;
    is_tech_spec?: boolean | null;
    is_required_egz?: boolean | null;
    is_required_kgd?: boolean | null;
}

export interface OktruAttributeResponse extends OktruAttributePayload {
    id: number;
}

export interface OktruAttributeListResponse {
    items: OktruAttributeResponse[];
    total: number;
    limit: number;
    offset: number;
}

export interface OktruAttributeValuePayload {
    attribute_id: number;
    value_code: string;
    value_ru: string;
    value_kk?: string | null;
    is_new?: boolean | null;
    is_changed?: boolean | null;
}

export interface OktruAttributeValueResponse extends OktruAttributeValuePayload {
    id: number;
}

export interface OktruEnstruLinkPayload {
    oktru_source_id: number;
    enstru_code: string;
}

export interface OktruEnstruLinkResponse extends OktruEnstruLinkPayload {
    id: number;
}

// ---------------------------------------------------------------------------
// Форма данных, которую отдаёт ModalPlanningNew.vue через emit('product-selected', ...)
// (см. интерфейсы Product/Category/ProductAttribute/EnstruEntry в самой модалке)
// ---------------------------------------------------------------------------

export interface OktruProductCategory {
    id: number;
    name: string;
    full_code: string;
    level?: number | null;
    parent?: number | null;
    [key: string]: unknown;
}

export interface OktruProductAttributeEntry {
    name: { kz?: string; ru?: string };
    value: { kz?: string; ru?: string };
    value_code: string;
    is_main?: boolean;
    is_tech_spec?: boolean;
    is_required_egz?: boolean;
    is_required_kgd?: boolean;
    [key: string]: unknown;
}

export interface OktruProductEnstruEntry {
    enstru: { code: string; [key: string]: unknown };
    [key: string]: unknown;
}

export interface OktruProductSelection {
    category?: OktruProductCategory | null;
    attributes_dict?: Record<string, OktruProductAttributeEntry>;
    enstrus?: OktruProductEnstruEntry[];
    [key: string]: unknown;
}

/**
 * Характеристики категории со всеми допустимыми значениями - то, что модалка
 * загружает по категории (эндпоинт attrs каталога). Именно эти данные являются
 * содержимым справочника ОКТРУ (oktru_attributes / oktru_attribute_values),
 * в отличие от attributes_dict, где лежат значения конкретного товара.
 */
export interface OktruCategoryAttributeValue {
    code: string;
    value?: { kz?: string; ru?: string };
    is_new?: boolean;
    is_changed?: boolean;
    [key: string]: unknown;
}

export interface OktruCategoryAttribute {
    code: string;
    name?: { kz?: string; ru?: string };
    values?: OktruCategoryAttributeValue[];
    data_type?: string;
    is_main?: boolean;
    is_new?: boolean;
    is_tech_spec?: boolean;
    is_required_egz?: boolean;
    is_required_kgd?: boolean;
    [key: string]: unknown;
}

/**
 * Счётчики показывают, сколько записей оказалось в БД по итогам сохранения:
 * и созданные сейчас, и сохранённые ранее (сервер отвечает на повтор 409).
 * Иначе при повторном выборе того же товара счётчики были бы нулевыми,
 * хотя данные в базе есть.
 */
export interface SaveProductSelectionResult {
    categorySaved: boolean;
    attributesSaved: number;
    valuesSaved: number;
    enstruLinksSaved: number;
    /** Читаемые (не переведённые) описания ошибок - для лога/отладки, не для показа пользователю "как есть" */
    errors: string[];
}

// ---------------------------------------------------------------------------
// Низкоуровневый helper - оборачивает Ax (callback-стиль) в Promise
// ---------------------------------------------------------------------------

interface AxResult<T> {
    ok: boolean;
    status: number | null;
    data: T | null;
    error?: I18nTemplate;
}

function authHeaders(): Record<string, string> {
    const token = store.getters.token;
    return token ? { Authorization: `Bearer ${token}` } : {};
}

function axRequest<T = unknown>(config: AxiosRequestConfig): Promise<AxResult<T>> {
    return new Promise((resolve) => {
        Ax<T>(
            config,
            (data, response) => {
                resolve({ ok: true, status: response.status, data });
            },
            (error, reason) => {
                let status: number | null = null;
                if (reason && typeof reason === 'object' && 'response' in reason) {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const response = (reason as any).response;
                    if (response && typeof response.status === 'number') {
                        status = response.status;
                    }
                }
                resolve({ ok: false, status, data: null, error });
            }
        );
    });
}

function describeError(res: { status: number | null; error?: I18nTemplate }): string {
    const statusPart = res.status ? `HTTP ${res.status}` : 'network error';
    return res.error ? `${statusPart}: ${res.error.key}` : statusPart;
}

// ---------------------------------------------------------------------------
// Запросы к sit-py
// ---------------------------------------------------------------------------

function upsertCategory(payload: OktruCategoryPayload): Promise<AxResult<OktruCategoryResponse>> {
    return axRequest<OktruCategoryResponse>({
        url: `${API_PY_BASE_URL}/categories`,
        method: 'POST',
        data: payload,
        headers: authHeaders(),
    });
}

function createAttribute(payload: OktruAttributePayload): Promise<AxResult<OktruAttributeResponse>> {
    return axRequest<OktruAttributeResponse>({
        url: `${API_PY_BASE_URL}/attributes`,
        method: 'POST',
        data: payload,
        headers: authHeaders(),
    });
}

function findAttributeByCode(
    categorySourceId: number,
    code: string
): Promise<AxResult<OktruAttributeListResponse>> {
    return axRequest<OktruAttributeListResponse>({
        url: `${API_PY_BASE_URL}/attributes`,
        method: 'GET',
        params: { oktru_category_source_id: categorySourceId, limit: 500 },
        headers: authHeaders(),
    });
}

function createAttributeValue(
    payload: OktruAttributeValuePayload
): Promise<AxResult<OktruAttributeValueResponse>> {
    return axRequest<OktruAttributeValueResponse>({
        url: `${API_PY_BASE_URL}/attribute-values`,
        method: 'POST',
        data: payload,
        headers: authHeaders(),
    });
}

function createEnstruLink(payload: OktruEnstruLinkPayload): Promise<AxResult<OktruEnstruLinkResponse>> {
    return axRequest<OktruEnstruLinkResponse>({
        url: `${API_PY_BASE_URL}/enstru-links`,
        method: 'POST',
        data: payload,
        headers: authHeaders(),
    });
}

/**
 * Гарантирует наличие характеристики в нашей БД и возвращает её id.
 * При 409 (характеристика с таким code для этой категории уже существует) - находит существующую через GET.
 */
async function ensureAttributeIdByPayload(
    payload: OktruAttributePayload
): Promise<{ id: number | null; status: number | null; error?: I18nTemplate }> {
    const categorySourceId = payload.oktru_category_source_id;
    const code = payload.code;

    const createRes = await createAttribute(payload);
    if (createRes.ok && createRes.data) {
        return { id: createRes.data.id, status: createRes.status };
    }

    if (createRes.status === 409) {
        const listRes = await findAttributeByCode(categorySourceId, code);
        const found = listRes.data?.items.find((item) => item.code === code);
        if (found) {
            return { id: found.id, status: createRes.status };
        }
        return { id: null, status: createRes.status, error: listRes.error };
    }

    return { id: null, status: createRes.status, error: createRes.error };
}

/** Характеристика конкретного товара (product.attributes_dict) */
function ensureAttributeId(
    categorySourceId: number,
    code: string,
    attr: OktruProductAttributeEntry
): Promise<{ id: number | null; status: number | null; error?: I18nTemplate }> {
    return ensureAttributeIdByPayload({
        oktru_category_source_id: categorySourceId,
        code,
        name_ru: attr.name?.ru || code,
        name_kk: attr.name?.kz,
        is_main: attr.is_main,
        is_tech_spec: attr.is_tech_spec,
        is_required_egz: attr.is_required_egz,
        is_required_kgd: attr.is_required_kgd,
    });
}

/**
 * Сохраняет характеристики категории со всеми допустимыми значениями -
 * это и есть содержимое справочника ОКТРУ для выбранной категории.
 */
async function saveCategoryAttributes(
    categorySourceId: number,
    attributes: OktruCategoryAttribute[],
    result: SaveProductSelectionResult,
    seenAttributeIds: Set<number>,
    seenValueKeys: Set<string>
): Promise<void> {
    for (const attr of attributes) {
        if (!attr || !attr.code) {
            continue;
        }
        try {
            const { id: attributeId, status, error } = await ensureAttributeIdByPayload({
                oktru_category_source_id: categorySourceId,
                code: attr.code,
                name_ru: attr.name?.ru || attr.code,
                name_kk: attr.name?.kz,
                data_type: attr.data_type,
                is_main: attr.is_main,
                is_new: attr.is_new,
                is_tech_spec: attr.is_tech_spec,
                is_required_egz: attr.is_required_egz,
                is_required_kgd: attr.is_required_kgd,
            });

            if (attributeId === null) {
                result.errors.push(
                    `Характеристика категории "${attr.code}": не удалось получить id (${describeError({ status, error })})`
                );
                continue;
            }
            if (!seenAttributeIds.has(attributeId)) {
                seenAttributeIds.add(attributeId);
                result.attributesSaved += 1;
            }

            for (const value of attr.values || []) {
                if (!value || !value.code) {
                    continue;
                }
                const valueRes = await createAttributeValue({
                    attribute_id: attributeId,
                    value_code: value.code,
                    value_ru: value.value?.ru || value.code,
                    value_kk: value.value?.kz,
                    is_new: value.is_new,
                    is_changed: value.is_changed,
                });
                // 409 - значение уже было сохранено ранее, для отчёта считаем его обработанным
                if (valueRes.ok || valueRes.status === 409) {
                    const valueKey = `${attributeId}:${value.code}`;
                    if (!seenValueKeys.has(valueKey)) {
                        seenValueKeys.add(valueKey);
                        result.valuesSaved += 1;
                    }
                } else {
                    result.errors.push(
                        `Значение "${value.code}" характеристики "${attr.code}": ${describeError(valueRes)}`
                    );
                }
            }
        } catch (e) {
            result.errors.push(`Характеристика категории "${attr.code}": ${String(e)}`);
        }
    }
}

// ---------------------------------------------------------------------------
// Основная функция
// ---------------------------------------------------------------------------

/**
 * Сохраняет товар, выбранный в модалке ОКТРУ, в наши таблицы (sit-py) - категорию,
 * характеристики, их значения и связи с ЕНСТРУ. Не бросает исключения из-за одной
 * "плохой" характеристики или связи - собирает ошибки в результат.
 */
export async function saveProductSelection(
    product: OktruProductSelection,
    categoryAttributes: OktruCategoryAttribute[] = []
): Promise<SaveProductSelectionResult> {
    const result: SaveProductSelectionResult = {
        categorySaved: false,
        attributesSaved: 0,
        valuesSaved: 0,
        enstruLinksSaved: 0,
        errors: [],
    };

    const category = product?.category;
    if (!category || typeof category.id !== 'number' || !category.full_code) {
        result.errors.push('Нет данных категории ОКТРУ (product.category) - сохранение отменено');
        return result;
    }

    const categoryPayload: OktruCategoryPayload = {
        source_id: category.id,
        parent_source_id: typeof category.parent === 'number' ? category.parent : null,
        name_ru: category.name,
        level: typeof category.level === 'number' ? category.level : null,
        full_code: category.full_code,
        is_leaf: true,
    };

    const categoryRes = await upsertCategory(categoryPayload);
    if (categoryRes.ok) {
        result.categorySaved = true;
    } else {
        result.errors.push(`Категория ОКТРУ (source_id=${category.id}): ${describeError(categoryRes)}`);
    }

    // уже учтённые в счётчиках записи - чтобы одна и та же характеристика/значение
    // не считались дважды (справочник категории и карточка товара пересекаются)
    const seenAttributeIds = new Set<number>();
    const seenValueKeys = new Set<string>();
    const seenEnstruCodes = new Set<string>();

    // Справочник характеристик категории со всеми допустимыми значениями
    await saveCategoryAttributes(category.id, categoryAttributes || [], result, seenAttributeIds, seenValueKeys);

    // Дополнительно - характеристики конкретного товара (их значения могут отсутствовать
    // в справочнике категории); повторы отсекаются сервером через 409
    const attributesDict = product.attributes_dict || {};
    for (const code of Object.keys(attributesDict)) {
        const attr = attributesDict[code];
        try {
            const { id: attributeId, status, error } = await ensureAttributeId(category.id, code, attr);
            if (attributeId === null) {
                result.errors.push(
                    `Характеристика "${code}": не удалось получить id (${describeError({ status, error })})`
                );
                continue;
            }
            if (!seenAttributeIds.has(attributeId)) {
                seenAttributeIds.add(attributeId);
                result.attributesSaved += 1;
            }

            const valuePayload: OktruAttributeValuePayload = {
                attribute_id: attributeId,
                value_code: attr.value_code,
                value_ru: attr.value?.ru || '',
                value_kk: attr.value?.kz,
            };
            const valueRes = await createAttributeValue(valuePayload);
            if (valueRes.ok || valueRes.status === 409) {
                const valueKey = `${attributeId}:${attr.value_code}`;
                if (!seenValueKeys.has(valueKey)) {
                    seenValueKeys.add(valueKey);
                    result.valuesSaved += 1;
                }
            } else {
                result.errors.push(`Значение характеристики "${code}": ${describeError(valueRes)}`);
            }
        } catch (e) {
            result.errors.push(`Характеристика "${code}": ${String(e)}`);
        }
    }

    const enstrus = product.enstrus || [];
    for (const entry of enstrus) {
        const enstruCode = entry?.enstru?.code;
        if (!enstruCode) {
            continue;
        }
        try {
            const linkRes = await createEnstruLink({
                oktru_source_id: category.id,
                enstru_code: enstruCode,
            });
            if (linkRes.ok || linkRes.status === 409) {
                if (!seenEnstruCodes.has(enstruCode)) {
                    seenEnstruCodes.add(enstruCode);
                    result.enstruLinksSaved += 1;
                }
            } else {
                result.errors.push(`Связь с ЕНСТРУ "${enstruCode}": ${describeError(linkRes)}`);
            }
        } catch (e) {
            result.errors.push(`Связь с ЕНСТРУ "${enstruCode}": ${String(e)}`);
        }
    }

    return result;
}

export default saveProductSelection;
